/**
 * External lead API client (Phase 105, Plan 105-03).
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts. Doing
 * so would clear localStorage.monksheets_auth_token on any 401 (see
 * client.ts:28), which would log out the internal Google-SSO user whenever
 * an external visitor hits /api/external/leads without a session. We use
 * fetch() directly with credentials:'include' so the HttpOnly external
 * session cookie is sent, but no internal token state is touched.
 *
 * `X-Audience: external` is mandatory on every call — `authenticateAny`
 * defaults to internal-preferred when neither the header nor a `/external/`
 * Referer is present (`authenticateAny.ts:71-75`), which would resolve an
 * admin browser holding both sessions as internal and 403 at
 * `requireExternalActor`. `externalBoard.ts` — not the header-less
 * `externalProjects.ts` — is the precedent this module follows.
 *
 * Read-only: no mutation function is exported. There is no write path on
 * `/api/external/leads*` (SC-3/D-06).
 */
import { ExternalApiError } from './externalAuth';
// Type-only import: erased at build time, so it creates no runtime edge to
// `./leads` (which itself imports `./client`) — reusing the 27-field `Lead`
// shape this way cannot violate the Landmine #8 isolation contract, while
// avoiding a duplicated interface that could silently drift from the
// backend payload.
import type { Lead, LeadStage } from './leads';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

async function externalFetch<T>(method: string, path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // External-portal audience hint so authenticateAny resolves the
      // external session even when an internal session also exists in the
      // browser. See module header — mandatory on every call.
      'X-Audience': 'external',
    },
  });
  if (!res.ok) {
    type ErrorPayload = { error?: string; message?: string };
    const payload = (await res.json().catch(() => ({}))) as ErrorPayload;
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
    });
  }
  return (await res.json()) as T;
}

export interface ExternalLeadListResponse {
  data: Lead[];
  total: number;
  // Phase 105, Plan 105-10 / D-13: `GET /admin/settings` (the source of
  // `lead_staleness_days`) is admin-only, so the external page cannot read
  // it directly. The backend resolves it server-side and carries it here so
  // the board can mark stale leads against the real admin-configured
  // threshold instead of a hardcoded guess.
  leadStalenessDays: number;
  // MKS-345 — the configured pipeline. The columns are configuration now, so
  // without this the external board would have no way to know which exist.
  stages: LeadStage[];
}

export async function fetchExternalLeads(): Promise<ExternalLeadListResponse> {
  const res = await externalFetch<ExternalLeadListResponse>('GET', '/external/leads');
  // Explicit passthrough (not a bare re-return) so `leadStalenessDays`
  // reaching the caller is visible at this call site, mirroring the
  // destructuring style already used server-side in
  // `externalLeadService.ts`'s `listLeadsForExternalActor`.
  return {
    data: res.data,
    total: res.total,
    stages: res.stages ?? [],
    leadStalenessDays: res.leadStalenessDays,
  };
}

export async function fetchExternalLead(id: number): Promise<Lead> {
  const res = await externalFetch<{ data: Lead }>('GET', `/external/leads/${id}`);
  return res.data;
}
